import { logger } from '../../../../../../utils/common/logger'
import { Store } from '../../../../../../utils/storage/keys'
import { IdentityEvent } from '../../../../../common/events/identity'
import { LoginService } from '../../services/login-service'
export interface ISuccessResponse {
    Profile: any,
    Data: any,
    access_token: string,
    jwttoken: string
}

export interface ISignInAccountInfo {
    givenName: string,
    idToken: string,
    surname: string
}

export interface ISignInErrorInfo {
    errorMessage: string
}

interface INotification {
    displayed: boolean,
    reason: string,
    type: string
}

interface IWindow {
    handleMicosoftSignIn: (signInAccountInfo: ISignInAccountInfo,
                           signInErrorInfo: ISignInErrorInfo) => void
    onload: () => void
    ms: any
    location: any
}

declare let window: IWindow

export class MicrosoftSocialLogin {
    constructor(private microsoftLoginId: string, private apiKey: string, private lrObject: any) {}

    /**
     * Initialize Microsoft social login
     */
    public init() {
        const accessToken = localStorage.getItem(Store.LR_TOKEN_KEY)
        const loginUrl = `${window.location.origin}/sign-in/`

        // check if microsoft script is loaded and user is not logged in
        if (window.ms && this.microsoftLoginId && !accessToken) {

            const result = window.ms.auth.initialize({
                callback: 'handleLiveTapResponse',
                client_id: this.microsoftLoginId,
                login_uri: loginUrl
            })

            if (result.result === 'success') {
                // microsoft edge sign in prompt
                window.ms.auth.prompt('right', (notification: INotification) => {
                    const reason = notification.reason
                    if (notification.type === 'display' && !notification.displayed) {
                        if ( reason === 'browser_not_supported' ) {
                            logger('info', 'Microsoft Edge prompt not supported in browser')
                        }
                    } else if (notification.type === 'skipped') {
                        if (reason === 'user_cancel') {
                            logger('info', 'User cancelled Microsoft Edge prompt')
                        }
                    } else if (notification.type === 'dismissed') {
                        if (reason === 'credential_returned') {
                            logger('info', 'Microsoft Edge prompt login credentials returned')
                        }
                    }
                })

                // listens for microsoft login success callback
                addEventListener(IdentityEvent.MICROSOFT_LOGIN_CALLBACK, async (event: CustomEvent) => {
                    if (event && event.detail) {
                        const eventDetail = event.detail
                        await this.handleMicrosoftSignIn(eventDetail.signInAccountInfo, eventDetail.signInErrorInfo)
                    }
                })
            } else {
                logger('error', 'Microsoft login initialization failed', result)
            }
        }
    }

    /**
     * Microsoft sign in callback
     *
     * @param signInAccountInfo
     * @param signInErrorInfo
     * @returns
     */
    public async handleMicrosoftSignIn(signInAccountInfo: ISignInAccountInfo, signInErrorInfo: ISignInErrorInfo) {
        if (signInErrorInfo) {
            // sign in error
            logger('error', 'Microsoft login failed', signInErrorInfo.errorMessage)
            return
        }

        if (signInAccountInfo) {
            try {
                // sign in successful
                const idToken = signInAccountInfo.idToken
                const providerName = 'microsoft'
                // tslint:disable-next-line: max-line-length
                const jwtApiResponse = await fetch(`https://api.loginradius.com/api/v2/access_token/jwt?key=${this.apiKey}&id_token=${idToken}&providername=${providerName}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    method: 'GET'
                })
                const jwtApiResult = await jwtApiResponse.json()
                const accessToken = jwtApiResult.access_token

                this.lrObject.api.socialLogin(
                    {
                        token: accessToken
                    },
                    this.socialLoginSuccessResponse,
                    this.socialLoginErrorResponse,
                    async () => {
                        await this.socialLoginMissingField(signInAccountInfo, accessToken)
                    }
                )
            } catch (err) {
                logger('error', 'Microsoft social sign in error:', err)
            }
        }
    }

    /**
     * Calls when sign in is successful
     *
     * @param response
     */
    public async socialLoginSuccessResponse(response: ISuccessResponse) {
        logger('info', 'Microsoft social sign in successful', response)
        LoginService.setRedirectUrl(location.href)
        await LoginService.handleLoginSuccess(response)
    }

    /**
     * Calls when there is a sign in error
     * @param error
     */

    public socialLoginErrorResponse(error: any) {
        throw new Error(error.Description)
    }

    /**
     * Calls when there are missing fields in the profile
     *
     * @param signInAccountInfo
     * @param accessToken
     */
    public async socialLoginMissingField(signInAccountInfo: ISignInAccountInfo, accessToken: string) {
        logger('info', 'Microsoft social sign in missing required fields')
        const data = {
            FirstName: signInAccountInfo.givenName,
            LastName: signInAccountInfo.surname
        }
        // update profile
        // tslint:disable-next-line: max-line-length
        const updateProfileCall = await fetch(`https://api.loginradius.com/identity/v2/auth/account?apiKey=${this.apiKey}`, {
            body: JSON.stringify(data),
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            method: 'PUT'
        })
        await updateProfileCall.json()

        // call social login again
        this.lrObject.api.socialLogin(
            {
                token: accessToken
            },
            this.socialLoginSuccessResponse,
            this.socialLoginErrorResponse
        )
    }
}
